import { trackConfig, type IFramework } from '@/framework';
import {
    LayerType,
    VectorType,
    type ILayerProps,
    type IVectorBorderRadiusProps,
    type IVectorLayerProps,
} from '@/version-types';
import { normalizeBorderRadius, pushHistory, updateLayer } from '../utils';

export interface IBorderRadius {
    /**
     * 设置矢量图层圆角
     *
     * @param layerID - 图层 ID
     * @param value - 圆角值
     * @param options - 配置项
     *
     *  - `options.corner` - 目标圆角，不填则使用所有圆角
     *  - `options.writeHistory` - 是否写入历史记录
     */
    setBorderRadius: (
        layerID: string,
        value: number,
        options?: { corner?: keyof IVectorBorderRadiusProps; writeHistory?: boolean },
    ) => ILayerProps | void;
}

export class BorderRadius implements IBorderRadius {
    private prevLayer: ILayerProps | null = null;

    constructor(private framework: IFramework) {}

    setBorderRadius(
        layerID: string,
        value: number,
        options: { corner?: keyof IVectorBorderRadiusProps; writeHistory?: boolean } = {},
    ) {
        const prevLayer = (() => {
            if (this.prevLayer) return this.prevLayer;
            this.prevLayer = this.framework.getLayer(layerID, true);
            return this.prevLayer;
        })();

        if (!prevLayer || prevLayer.layerType !== LayerType.VECTOR_LAYER) return;

        const prevLayerData = prevLayer.data as IVectorLayerProps;
        const { vectorType } = prevLayerData;

        if (vectorType !== VectorType.Rect) return;

        const { writeHistory = true } = options;

        if (writeHistory) {
            const nextLayer = this.framework.getLayer(layerID, true);

            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.VECTOR_LAYER].radius);
            this.prevLayer = null;

            return nextLayer;
        }

        const nextLayerData: IVectorLayerProps = { ...prevLayerData };

        const { corner } = options;
        if (corner) {
            const nextBorderRadius = (() => {
                const borderRadius = normalizeBorderRadius(prevLayerData);
                return { ...borderRadius, [corner]: value };
            })();

            nextLayerData.borderRadius = nextBorderRadius;
        } else {
            nextLayerData.radius = value;
            nextLayerData.borderRadius = {
                leftTop: value,
                rightTop: value,
                leftBottom: value,
                rightBottom: value,
            };
        }

        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };
        updateLayer(this.framework, nextLayer);

        return nextLayer;
    }
}
